import React from "react";

/**
 * ErrorBoundary — catches render-time crashes anywhere below and shows a
 * friendly recovery card instead of a blank white page. Placed at the top
 * of the App tree so a single module-eval error, undefined property access,
 * or lifecycle throw never blanks the SPA (as the api.js TDZ bug did in
 * iter 79). Also reports the error via a lightweight beacon so we can spot
 * regressions in production.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && (error.message || String(error))) || "Something went wrong",
      stack: (error && error.stack) || "",
    };
  }

  componentDidCatch(error, info) {
    // Best-effort telemetry — swallow all errors so the boundary itself
    // never re-throws.
    try {
      const payload = {
        message: (error && (error.message || String(error))) || "unknown",
        component_stack: (info && info.componentStack) || "",
        url: typeof window !== "undefined" ? window.location.href : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ts: new Date().toISOString(),
      };
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/telemetry/frontend-error`;
      // Prefer sendBeacon so it survives page unload; fall back to fetch.
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else if (typeof fetch !== "undefined") {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* swallow */
    }
  }

  reload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  goHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isProd = process.env.NODE_ENV === "production";

    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-white via-emerald-50/30 to-white px-4"
      >
        <div className="max-w-lg w-full border border-navy/10 rounded-2xl bg-white shadow-[0_20px_60px_rgba(15,61,46,0.10)] p-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700 mb-2">
            VenturaZ · Recovery
          </div>
          <div className="font-heading text-2xl text-ink leading-tight">
            Something broke on this page.
          </div>
          <div className="text-sm text-ink-soft mt-3">
            Sorry — the page hit an unexpected error and couldn&apos;t render.
            Your data is safe. You can reload the page or head back to the
            homepage and try again.
          </div>

          {!isProd && this.state.message && (
            <details
              className="mt-4 bg-navy/[0.03] rounded border border-navy/10 p-3 text-[11px] font-mono text-ink-muted"
              data-testid="app-error-boundary-details"
            >
              <summary className="cursor-pointer text-ink-soft">
                Developer details
              </summary>
              <div className="mt-2 whitespace-pre-wrap break-words">
                {this.state.message}
              </div>
              {this.state.stack && (
                <pre className="mt-2 max-h-48 overflow-auto text-[10px] leading-relaxed">
                  {this.state.stack.split("\n").slice(0, 12).join("\n")}
                </pre>
              )}
            </details>
          )}

          <div className="mt-6 flex gap-3 flex-wrap">
            <button
              onClick={this.reload}
              data-testid="app-error-boundary-reload"
              className="px-4 py-2 rounded-md bg-navy text-white text-sm font-heading hover:bg-[#256E58] transition-colors"
            >
              Reload page
            </button>
            <button
              onClick={this.goHome}
              data-testid="app-error-boundary-home"
              className="px-4 py-2 rounded-md bg-white text-ink border border-navy/15 text-sm font-heading hover:border-gold/50 hover:bg-gold/5 transition-colors"
            >
              Back to home
            </button>
            <a
              href="mailto:support@venturaz.com"
              className="px-4 py-2 text-sm font-mono text-ink-muted underline underline-offset-4"
            >
              support@venturaz.com
            </a>
          </div>
        </div>
      </div>
    );
  }
}
